import WebGL from "three/examples/jsm/capabilities/WebGL.js";
import { Viewer } from "./viewer.js";
import { SimpleDropzone } from "simple-dropzone";
import { ValidationController } from "./validation-controller.js";
import queryString from "query-string";
import { getProfilePicture } from "./profilepicture.js";

if (!(window.File && window.FileReader && window.FileList && window.Blob)) {
  console.error("The File APIs are not fully supported in this browser.");
} else if (!WebGL.isWebGLAvailable()) {
  console.error("WebGL is not supported in this browser.");
}

class App {
  /**
   * @param  {Element} el
   * @param  {Location} location
   */
  constructor(el, location, customModel, customName, index) {
    const hash = location.hash ? queryString.parse(location.hash) : {};
    const model = customModel ? customModel : hash.model;
    this.options = {
      name: customName ? customName : hash.name,
      index: index ? index : hash.index,
      kiosk: Boolean(hash.kiosk),
      // pfp == profile picture
      pfp: Boolean(hash.pfp),
      model: model || "",
      environment: hash.bg,
      preset: hash.preset || "",
      cameraPosition: hash.cameraPosition
        ? hash.cameraPosition.split(",").map(Number)
        : null,
    };
    this.el = el;
    this.viewer = null;
    this.viewerEl = null;
    this.spinnerEl = el.querySelector(".spinner");
    this.dropEl = el.querySelector(".dropzone");
    this.inputEl = el.querySelector("#file-input");
    this.validationCtrl = new ValidationController(el);

    if (!customModel) this.createDropzone();
    // this.hideSpinner();

    const options = this.options;

    const headerEl = document.querySelector("header");
    headerEl.style.display = "none";

    if (options.model) {
      this.showSpinner();
      this.view(options.model, "", new Map());
    }
  }

  /**
   * Sets up the drag-and-drop controller.
   */
  createDropzone() {
    const dropCtrl = new SimpleDropzone(this.dropEl, this.inputEl);
    dropCtrl.on("drop", ({ files }) => this.load(files));
    dropCtrl.on("dropstart", () => this.showSpinner());
    dropCtrl.on("droperror", () => this.hideSpinner());
  }

  /**
   * Sets up the view manager.
   * @return {Viewer}
   */
  createViewer() {
    this.viewerEl = document.createElement("div");
    this.viewerEl.classList.add("viewer");
    this.dropEl.innerHTML = "";
    this.dropEl.appendChild(this.viewerEl);
    this.viewer = new Viewer(this.viewerEl, this.options);
    return this.viewer;
  }

  /**
   * Loads a fileset provided by user action.
   * @param  {Map<string, File>} fileMap
   */
  load(fileMap) {
    let rootFile;
    let rootPath;
    Array.from(fileMap).forEach(([path, file]) => {
      if (file.name.match(/\.(gltf|glb)$/)) {
        rootFile = file;
        rootPath = path.replace(file.name, "");
      }
    });

    if (!rootFile) {
      this.onError("No .gltf or .glb asset found.");
    }

    this.view(rootFile, rootPath, fileMap);
  }

  /**
   * Passes a model to the viewer, given file and resources.
   * @param  {File|string} rootFile
   * @param  {string} rootPath
   * @param  {Map<string, File>} fileMap
   */
  view(rootFile, rootPath, fileMap) {
    // Animation controls
    let done = false;
    let currentIndex = parseInt(
      document.querySelector("#indexAnimation").textContent
    );
    const arrowsAnimation = document.querySelector(".arrowsAnimation");
    const prevAnimation = document.querySelector("#prevAnimation");
    const nextAnimation = document.querySelector("#nextAnimation");
    const autorotate = document.querySelector("#autorotate");
    const name = document.querySelector("#nftName");
    name.textContent = `${this.options.name} | ${this.options.index}`;

    if (this.viewer) this.viewer.clear();

    const viewer = this.viewer || this.createViewer();

    const fileURL =
      typeof rootFile === "string" ? rootFile : URL.createObjectURL(rootFile);

    const cleanup = () => {
      this.hideSpinner();
      if (typeof rootFile === "object") URL.revokeObjectURL(fileURL);
    };

    viewer
      .load(fileURL, rootPath, fileMap)
      .catch(() => {})
      .then(() => {
        done = true;
      })
      .catch((e) => this.onError(e))
      .then((gltf) => {
        if (!this.options.kiosk) {
          this.validationCtrl.validate(fileURL, rootPath, fileMap, gltf);
        }
        cleanup();

        // Show animation constrols only if clips exist and promise is resolved and is not profile picture view
        if (done && !this.options.pfp) {
          autorotate.style.display = "flex";
          document.querySelector("#autorotate input").checked = true;
          updateClip(true);

          if (viewer.clips.length > 1) arrowsAnimation.style.display = "flex";

          // Animation functions
          prevAnimation.addEventListener("click", function handleClick(e) {
            e.stopImmediatePropagation();
            prevClip();
          });
          nextAnimation.addEventListener("click", function handleClick(e) {
            e.stopImmediatePropagation();
            nextClip();
          });

          function prevClip() {
            currentIndex = parseInt(
              document.querySelector("#indexAnimation").textContent
            );
            if (currentIndex <= 0) currentIndex = viewer.clips.length - 1;
            else if (currentIndex > 0) currentIndex--;
            updateClip(false);
          }
          function nextClip() {
            currentIndex = parseInt(
              document.querySelector("#indexAnimation").textContent
            );
            if (currentIndex == viewer.clips.length - 1) currentIndex = 0;
            else if (currentIndex >= 0) currentIndex++;
            updateClip(false);
          }

          function updateClip(firstLoad) {
            if (firstLoad) viewer.playClip(viewer.clips[0], 0, true);
            else
              viewer.playClip(viewer.clips[currentIndex], currentIndex, true);
          }
          // profile picture
        } else if (done && this.options.pfp) {
          // trigger resize function to solve cropped height
          setTimeout(() => {
            document.querySelector("body").classList.add("pfp-view--loaded");
            viewer.resize();
          }, 1000);

          const download = document.getElementById("pfp-download");
          const pfpName = this.options.name;
          const togglePfpInfoPanel = document.getElementById("pfp-info");
          const closePfpInfoPanel = document.getElementById(
            "pfp-info-panel-close"
          );
          const element = document.getElementById("pfp-info-panel");

          download.addEventListener("click", function (e) {
            const imageData = viewer.getDataURL();
            // document.querySelector('.gui-wrap').style.display = 'none';
            getProfilePicture(pfpName);
          });
          togglePfpInfoPanel.addEventListener("click", function (e) {
            e.preventDefault();
            element.classList.toggle("active");
            this.classList.toggle("active");
          });
          closePfpInfoPanel.addEventListener("click", function (e) {
            togglePfpInfoPanel.classList.remove("active");
            element.classList.remove("active");
          });
          document.addEventListener(
            "click",
            function handleClickOutside(event) {
              if (
                !element.contains(event.target) &&
                !togglePfpInfoPanel.contains(event.target)
              ) {
                togglePfpInfoPanel.classList.remove("active");
                element.classList.remove("active");
              }
            }
          );
        }
      });
  }

  /**
   * @param  {Error} error
   */
  onError(error) {
    let message = (error || {}).message || error.toString();
    if (message.match(/ProgressEvent/)) {
      message =
        "Unable to retrieve this file. Check JS console and browser network tab.";
    } else if (message.match(/Unexpected token/)) {
      message = `Unable to parse file content. Verify that this file is valid. Error: "${message}"`;
    } else if (error && error.target && error.target instanceof Image) {
      message = "Missing texture: " + error.target.src.split("/").pop();
    }
    window.alert(message);
    console.error(error);
  }

  showSpinner() {
    this.spinnerEl.style.display = "";
  }

  hideSpinner() {
    this.spinnerEl.style.display = "none";
  }
}
document.cookie = "SameSite=None; Secure";
document.addEventListener("DOMContentLoaded", () => {
  let customModel = "";
  let customName = "";
  window.addEventListener("message", handleMessage);
  function handleMessage(e) {
    // Check origin nft / http://127.0.0.1:5173 https://nft.cosmicchamps.com
    if (e.origin != "https://nft.cosmicchamps.com") {
      return;
    }
    // Send reply to source of message
    var state = document.readyState;
    if (state === "complete") {
      customName = e.data.name;
      customModel = e.data.glbURL;
      let app = new App(document.body, location, customModel, customName);
      e.source.postMessage("Message received, iframe app reponded", e.origin);
    } else {
      setTimeout(() => {
        customName = e.data.name;
        customModel = e.data.glbURL;
        let app = new App(document.body, location, customModel, customName);
      }, 5000);
      e.source.postMessage("Sending msg after 5s", e.origin);
    }
  }

  let app = new App(document.body, location, customModel, customName);
});
